// src/components/fuel/TransactionModal.js
import React, { useState } from 'react';
import { FUEL_META } from './constants';
import { Modal, Field, ModalFooter } from './modalComponents';
import { fmtUSD } from './formatters';
import { Truck, Fuel, User, Building, Mail, Phone, MapPin } from 'lucide-react';

const TransactionModal = ({ 
  isOpen, 
  onClose, 
  editingTx, 
  txForm, 
  setTxForm, 
  inventory, 
  aircraft, 
  owners, 
  staff, 
  trucks, 
  onSave, 
  saving,
  isVisitor = false,
  visitorTailNumber = null,
  visitorBilling = {},
  setVisitorBilling = null
}) => {
  const [localBilling, setLocalBilling] = useState({
    contact_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });

  if (!isOpen) return null;

  const billing = visitorBilling || localBilling;
  const setBilling = setVisitorBilling || setLocalBilling;

  const safeAircraft = Array.isArray(aircraft) ? aircraft : [];
  const safeOwners = Array.isArray(owners) ? owners : [];
  const safeStaff = Array.isArray(staff) ? staff : [];
  const safeTrucks = Array.isArray(trucks) ? trucks : [];
  const safeInventory = Array.isArray(inventory) ? inventory : [];

  const setForm = (key, val) => setTxForm(prev => ({ ...prev, [key]: val }));
  const setBillingField = (key, val) => setBilling(prev => ({ ...prev, [key]: val }));

  const total = txForm.gallons && txForm.price_per_gallon 
    ? parseFloat(txForm.gallons) * parseFloat(txForm.price_per_gallon) 
    : null;

  const selectedTruck = safeTrucks.find(t => String(t.id) === String(txForm.truck_id));
  
  const handleTruckChange = (truckId) => {
    if (truckId) {
      const truck = safeTrucks.find(t => String(t.id) === String(truckId));
      if (truck) {
        setForm('truck_id', truck.id);
        setForm('fuel_type', truck.fuel_type);
        const price = truck.price_per_gallon;  // ← Utilise price_per_gallon
        if (price) {
          setForm('price_per_gallon', String(price));
        }
      }
    } else {
      setForm('truck_id', '');
      const defaultTank = safeInventory[0];
      if (defaultTank) {
        setForm('fuel_type', defaultTank.fuel_type);
        setForm('price_per_gallon', defaultTank.price_per_gallon ? String(defaultTank.price_per_gallon) : '');
      }
    }
  };

  const handleFuelTypeChange = (fuelType) => {
    const selected = safeInventory.find(i => i.fuel_type === fuelType);
    setForm('fuel_type', fuelType);
    if (selected?.price_per_gallon) {
      setForm('price_per_gallon', String(selected.price_per_gallon));
    }
    if (txForm.truck_id) {
      setForm('truck_id', '');
    }
  };

  const isSaveDisabled = () => {
    if (!txForm.gallons || !txForm.price_per_gallon) return true;
    if (isVisitor && !billing.contact_name) return true;
    if (!isVisitor && !txForm.aircraft_id) return true;
    return false;
  };

  const handleSaveWithBilling = () => {
    if (isVisitor && billing) {
      onSave(billing);
    } else {
      onSave();
    }
  };

  return (
    <Modal
      title={isVisitor 
        ? `⛽ Fuel Visitor — ${visitorTailNumber || 'Aircraft'}` 
        : (editingTx ? `✏️ Edit Transaction · ${editingTx.transaction_number || editingTx.id?.slice(0, 8)}` : '⛽ Fuel Aircraft')}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
        
        {/* Mode Visiteur : Info */}
        {isVisitor && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-blue-300 text-sm flex items-center gap-2">
              <Fuel size={16} />
              Adding fuel to visitor aircraft: <span className="font-mono font-bold text-white">{visitorTailNumber}</span>
            </p>
          </div>
        )}

        {/* Billing Information - only for visitors */}
        {isVisitor && (
          <div className="space-y-3 border border-blue-500/30 rounded-lg p-4 bg-blue-500/5">
            <div className="flex items-center gap-2 mb-2">
              <User size={16} className="text-blue-400" />
              <h4 className="text-blue-400 font-semibold text-sm">📋 Billing Information (Required for invoice)</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Field label="Contact Name *">
                  <div className="relative">
                    <User size={14} className="absolute left-3 top-2.5 text-gray-500" />
                    <input 
                      type="text" 
                      placeholder="John Smith"
                      value={billing.contact_name || ''}
                      onChange={e => setBillingField('contact_name', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-9 pr-3 text-white text-sm"
                    />
                  </div>
                </Field>
              </div>
              <div>
                <Field label="Company">
                  <div className="relative">
                    <Building size={14} className="absolute left-3 top-2.5 text-gray-500" />
                    <input 
                      type="text" 
                      placeholder="ABC Aviation"
                      value={billing.company_name || ''}
                      onChange={e => setBillingField('company_name', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-9 pr-3 text-white text-sm"
                    />
                  </div>
                </Field>
              </div>
              <div>
                <Field label="Email">
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-2.5 text-gray-500" />
                    <input 
                      type="email" 
                      placeholder="pilot@example.com"
                      value={billing.email || ''}
                      onChange={e => setBillingField('email', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-9 pr-3 text-white text-sm"
                    />
                  </div>
                </Field>
              </div>
              <div>
                <Field label="Phone">
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-2.5 text-gray-500" />
                    <input 
                      type="tel" 
                      placeholder="(830) 336-2518"
                      value={billing.phone || ''}
                      onChange={e => setBillingField('phone', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-9 pr-3 text-white text-sm"
                    />
                  </div>
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Address">
                  <div className="relative">
                    <MapPin size={14} className="absolute left-3 top-2.5 text-gray-500" />
                    <input 
                      type="text" 
                      placeholder="123 Aviation Way"
                      value={billing.address || ''}
                      onChange={e => setBillingField('address', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-9 pr-3 text-white text-sm"
                    />
                  </div>
                </Field>
              </div>
              <div>
                <Field label="City">
                  <input 
                    type="text" 
                    placeholder="San Antonio"
                    value={billing.city || ''}
                    onChange={e => setBillingField('city', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Field label="State">
                    <input 
                      type="text" 
                      placeholder="TX"
                      maxLength={2}
                      value={billing.state || ''}
                      onChange={e => setBillingField('state', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
                    />
                  </Field>
                </div>
                <div>
                  <Field label="ZIP">
                    <input 
                      type="text" 
                      placeholder="78216"
                      value={billing.zip || ''}
                      onChange={e => setBillingField('zip', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aircraft selection - hidden for visitors */}
        {!isVisitor && (
          <Field label="Aircraft *">
            <select 
              value={txForm.aircraft_id || ''} 
              onChange={e => setForm('aircraft_id', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
            >
              <option value="">— Select aircraft —</option>
              {safeAircraft.map(a => (
                <option key={a.id} value={a.id}>
                  {a.tail_number}{a.model ? ` · ${a.model}` : ''}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/* Owner selection - hidden for visitors */}
        {!isVisitor && (
          <Field label="Owner">
            <select 
              value={txForm.owner_id || ''} 
              onChange={e => setForm('owner_id', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
            >
              <option value="">— Select owner —</option>
              {safeOwners.map(o => (
                <option key={o.id} value={o.id}>
                  👤 {o.company_name || `${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/* Fuel Source */}
        <div className="bg-gray-700/30 rounded-lg p-3">
          <label className="block text-gray-400 text-xs mb-2 font-medium">⛽ Fuel Source</label>
          <div className="flex gap-4 mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio"
                name="fuelSource"
                checked={!txForm.truck_id}
                onChange={() => handleTruckChange('')}
                className="text-blue-500"
              />
              <Fuel size={14} className="text-blue-400" />
              <span className="text-gray-300 text-sm">Station Tank</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio"
                name="fuelSource"
                checked={!!txForm.truck_id}
                onChange={() => {
                  if (!txForm.truck_id && safeTrucks.length > 0) {
                    handleTruckChange(safeTrucks[0].id);
                  } else if (safeTrucks.length === 0) {
                    alert('No fuel trucks available. Please add a truck first.');
                  }
                }}
                className="text-blue-500"
              />
              <Truck size={14} className="text-green-400" />
              <span className="text-gray-300 text-sm">Fuel Truck</span>
            </label>
          </div>

          {/* Truck selection dropdown - shown when truck mode is active */}
          {txForm.truck_id && safeTrucks.length > 0 && (
            <div className="mt-2">
              <select 
                value={txForm.truck_id || ''} 
                onChange={e => handleTruckChange(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
              >
                {safeTrucks.map(t => (
                  <option key={t.id} value={t.id}>
                    🚛 {t.truck_number || t.truck_name || t.id} - {t.fuel_type} - {Math.round(t.current_volume_gallons || 0)} gal available
                  </option>
                ))}
              </select>
              {selectedTruck && (
                <div className="mt-2 text-xs text-gray-400">
                  Truck fuel level: {Math.round(selectedTruck.current_volume_gallons || 0)} / {Math.round(selectedTruck.capacity_gallons || 0)} gal
                </div>
              )}
            </div>
          )}

          {/* Message if no trucks available */}
          {safeTrucks.length === 0 && (
            <div className="mt-2 text-xs text-yellow-400">
              No fuel trucks available. Please add a truck in Fuel Management.
            </div>
          )}
        </div>

        <Field label="Fuel Type *">
          <select
            value={txForm.fuel_type}
            onChange={e => handleFuelTypeChange(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
            disabled={!!txForm.truck_id}
          >
            {safeInventory.map(i => (
              <option key={i.fuel_type} value={i.fuel_type}>
                {FUEL_META[i.fuel_type]?.label || i.fuel_type} · {Math.round(i.current_volume_gallons || 0)} gal available
              </option>
            ))}
          </select>
          {txForm.truck_id && selectedTruck && (
            <p className="text-xs text-blue-400 mt-1">
              Fuel type: {selectedTruck.fuel_type} (from selected truck)
            </p>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Volume (gallons) *">
            <input 
              type="number" 
              min="0" 
              step="0.1" 
              placeholder="e.g. 50"
              value={txForm.gallons} 
              onChange={e => setForm('gallons', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm" 
              autoFocus
            />
            {selectedTruck && (
              <p className="text-xs text-gray-500 mt-1">
                Max available: {Math.round(selectedTruck.current_volume_gallons || 0)} gal
              </p>
            )}
          </Field>
          <Field label="Price / gallon *">
            <input 
              type="number" 
              min="0" 
              step="0.0001" 
              placeholder="e.g. 5.85"
              value={txForm.price_per_gallon} 
              onChange={e => setForm('price_per_gallon', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm" 
            />
          </Field>
        </div>

        {total !== null && total > 0 && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Subtotal:</span>
              <span className="text-green-400 font-bold text-lg">{fmtUSD(total)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">* Tax will be added on invoice</p>
          </div>
        )}

        <Field label="Staff">
          <select 
            value={txForm.staff_id || ''} 
            onChange={e => setForm('staff_id', e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white text-sm"
          >
            <option value="">— Self-service / terminal —</option>
            {safeStaff.map(s => (
              <option key={s.id} value={s.id}>
                👨‍✈️ {s.first_name} {s.last_name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <ModalFooter
        onCancel={onClose}
        onSave={handleSaveWithBilling}
        saving={saving}
        disabled={isSaveDisabled()}
        label={editingTx ? 'Save Changes' : 'Save Transaction'}
      />
    </Modal>
  );
};

export default TransactionModal;